import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faTrash,
  faInfoCircle,
  faCheck,
  faBan,
  faEye,
  faBars,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import { supabase, supabaseAdmin } from '../lib/supabaseClient';
import DriverInfoModal from '../components/DriverInfoModal';
import CompanyInfoModal from '../components/CompanyInfoModal';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

function Companies() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [companiesPerPage, setCompaniesPerPage] = useState(10);
  const [filters, setFilters] = useState({
    search: '',
    status: 'approved',
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDriverModalOpen, setIsDriverModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [editFormData, setEditFormData] = useState({
    company_name: '',
    tax_number: '',
    phone_number: '',
    address: '',
  });
  const [isDriverDetailsModalOpen, setIsDriverDetailsModalOpen] =
    useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [isCompanyDetailsModalOpen, setIsCompanyDetailsModalOpen] =
    useState(false);
  const [isDocumentApprovalModalOpen, setIsDocumentApprovalModalOpen] =
    useState(false);
  const [documentType, setDocumentType] = useState(null);
  const [documentUrl, setDocumentUrl] = useState(null);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const fetchCompanies = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_type', 'company');
      console.log(data);
      if (error) throw error;

      setCompanies(data || []);
      setFilteredCompanies(data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setError('Şirketler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchDrivers = async () => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_type', 'driver');

    if (error) {
      console.error('Error fetching drivers:', error);
    } else {
      setDrivers(data);
    }
  };

  const handleApproveCompany = async (companyId) => {
    try {
      const company = companies.find((c) => c.id === companyId);
      const pendingDocs = getPendingDocumentsCount(company);

      if (pendingDocs > 0) {
        setError(
          'Şirketi onaylamadan önce tüm belgeleri onaylamanız gerekmektedir.'
        );
        return;
      }

      const { error } = await supabaseAdmin
        .from('profiles')
        .update({ status: 'approved' })
        .eq('id', companyId);

      if (error) throw error;

      await fetchCompanies();
    } catch (error) {
      console.error('Error approving company:', error);
      setError('Şirket onaylanırken bir hata oluştu');
    }
  };

  const confirmBlockCompany = async (companyId) => {
    try {
      const { error } = await supabaseAdmin
        .from('profiles')
        .update({ status: 'rejected' })
        .eq('id', companyId);

      if (error) throw error;

      await fetchCompanies();
    } catch (error) {
      console.error('Error blocking company:', error);
      setError('Şirket engellenirken bir hata oluştu');
    }
  };

  useEffect(() => {
    filterCompanies();
  }, [companies, filters]);

  const filterCompanies = () => {
    let filtered = [...companies];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (company) =>
          company.company_name?.toLowerCase().includes(searchLower) ||
          company.tax_number?.toLowerCase().includes(searchLower)
      );
    }

    if (filters.status) {
      filtered = filtered.filter(
        (company) => company.status === filters.status
      );
    }

    setFilteredCompanies(filtered);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Pagination
  const indexOfLastCompany = currentPage * companiesPerPage;
  const indexOfFirstCompany = indexOfLastCompany - companiesPerPage;
  const currentCompanies = filteredCompanies.slice(
    indexOfFirstCompany,
    indexOfLastCompany
  );
  const totalPages = Math.ceil(filteredCompanies.length / companiesPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // Handle change in number of companies per page
  const handleCompaniesPerPageChange = (e) => {
    setCompaniesPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Reset to first page when filters or companiesPerPage change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, companiesPerPage]);

  const getPendingDocumentsCount = (company) => {
    return [
      company?.l2_document,
      company?.tio_document,
      company?.c2_document,
    ].filter((doc) => doc?.status === 'pending').length;
  };

  const handleAddNewCompany = () => {
    setSelectedCompany(null);
    setEditFormData({
      company_name: '',
      tax_number: '',
      phone_number: '',
      email: '',
      address: '',
      first_name: '',
      last_name: '',
    });
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      if (selectedCompany) {
        // Update existing company
        const { error } = await supabase
          .from('profiles')
          .update(editFormData)
          .eq('id', selectedCompany.id);

        if (error) throw error;
      } else {
        // Create new company
        // First create auth user
        const { data: authData, error: signUpError } =
          await supabaseAdmin.auth.admin.createUser({
            phone: editFormData.phone_number,
            email: editFormData.email || undefined,
            phone_confirm: true,
          });

        if (signUpError) throw signUpError;

        // Then create company profile
        const { error: profileError } = await supabase.from('profiles').insert([
          {
            id: authData.user.id,
            first_name: editFormData.first_name || editFormData.company_name,
            last_name: editFormData.last_name || '',
            company_name: editFormData.company_name,
            company: editFormData.company_name,
            tax_number: editFormData.tax_number,
            phone_number: editFormData.phone_number,
            email: editFormData.email,
            address: editFormData.address,
            user_type: 'company',
            status: 'pending',
            citizenship: '',
            is_profile_completed: true,
            created_at: new Date().toISOString(),
          },
        ]);

        if (profileError) throw profileError;
      }

      setIsEditModalOpen(false);
      fetchCompanies();
    } catch (error) {
      console.error('Error saving company:', error);
      setError(error.message || 'Şirket kaydedilirken bir hata oluştu');
    }
  };

  const handleAssignDriver = async (driverId) => {
    const { error } = await supabase
      .from('profiles')
      .update({ company_id: selectedCompany.id, user_type: 'company_driver' })
      .eq('id', driverId);

    if (error) {
      console.error('Error assigning driver:', error);
    } else {
      setIsDriverModalOpen(false);
    }
  };

  const handleDriverDetailsClick = (driver) => {
    setSelectedDriver(driver);
    setIsDriverDetailsModalOpen(true);
  };

  const handleCompanyDetailsClick = (company) => {
    setSelectedCompany(company);
    setIsCompanyDetailsModalOpen(true);
  };

  const handleApproveDocument = async (companyId, documentType) => {
    try {
      const { error } = await supabaseAdmin
        .from('profiles')
        .update({ [`${documentType}_document`]: { status: 'approved' } })
        .eq('id', companyId);

      if (error) throw error;

      setIsDocumentApprovalModalOpen(false);
      await fetchCompanies();
    } catch (error) {
      console.error('Error approving document:', error);
      setError('Belge onaylanırken bir hata oluştu');
    }
  };
  const deleteUser = async () => {
    const { data } = await supabaseAdmin.auth.admin.deleteUser(
      '6002144a-4715-4549-a9f6-44739b3a5e14',
      true
    );
  };
  useEffect(() => {
    deleteUser();
  }, []);

  const handleRejectDocument = async (companyId, documentType) => {
    try {
      const { error } = await supabaseAdmin
        .from('profiles')
        .update({ [`${documentType}_document`]: { status: 'rejected' } })
        .eq('id', companyId);

      if (error) throw error;

      setIsDocumentApprovalModalOpen(false);
      await fetchCompanies();
    } catch (error) {
      console.error('Error rejecting document:', error);
      setError('Belge reddedilirken bir hata oluştu');
    }
  };

  const openDocumentApprovalModal = (company, docType) => {
    setSelectedCompany(company);
    setDocumentType(docType);
    setDocumentUrl(company[`${docType}_document`]?.url || null);
    setIsDocumentApprovalModalOpen(true);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Top Navigation */}
      <header className="bg-white shadow">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button className="text-gray-500 lg:hidden">
                <FontAwesomeIcon icon={faBars} className="text-xl" />
              </button>
            </div>

            <div className="relative">
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none"
              >
                <span>{user?.email}</span>
                <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-between pb-6">
            <div>
              <h1 className="text-2xl font-medium text-gray-800">Şirketler</h1>
              <p className="text-sm text-gray-500">
                Toplam Şirket: {companies.length}
              </p>
            </div>
            <button
              onClick={() => handleAddNewCompany()}
              className="bg-[#4263EB] text-white px-4 py-2 rounded-md hover:bg-[#3b5bd9] flex items-center"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Yeni Şirket Ekle
            </button>
          </div>

          {/* Filter Section */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Şirket Ara
                </label>
                <input
                  type="text"
                  name="search"
                  placeholder="Şirket Adı veya Vergi No"
                  value={filters.search}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Durum
                </label>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tüm Durumlar</option>
                  <option value="pending">Onay Bekliyor</option>
                  <option value="approved">Onaylandı</option>
                  <option value="rejected">Engellendi</option>
                </select>
              </div>
            </div>
          </div>

          {/* Companies List */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {error && (
              <div className="p-4 text-red-700 bg-red-100">{error}</div>
            )}

            {loading ? (
              <div className="p-4 text-center">Şirketler yükleniyor...</div>
            ) : (
              <>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Şirket
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        İletişim
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Durum
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Belgeler
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Eylemler
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentCompanies.map((company) => (
                      <tr key={company.id}>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div>
                              <div className="font-medium text-gray-900">
                                {company.company_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                Vergi No: {company.tax_number}
                              </div>
                              <div className="text-xs text-gray-400">
                                Kayıt:{' '}
                                {new Date(
                                  company.created_at
                                ).toLocaleDateString('tr-TR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                })}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {company.phone_number}
                          </div>
                          <div className="text-sm text-gray-500">
                            {company.email}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                            ${
                              company.status === 'approved'
                                ? 'bg-green-100 text-green-800'
                                : company.status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {company.status === 'approved'
                              ? 'Onaylı'
                              : company.status === 'pending'
                                ? 'Onay Bekliyor'
                                : 'Engellendi'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 cursor-pointer"
                            onClick={() =>
                              company.status !== 'approved' &&
                              getPendingDocumentsCount(company) > 0 &&
                              handleCompanyDetailsClick(company)
                            }
                          >
                            {getPendingDocumentsCount(company)} Onay Bekliyor
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <button
                            onClick={() => handleCompanyDetailsClick(company)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                          >
                            <FontAwesomeIcon icon={faEye} className="mr-1" />
                            Detaylar
                          </button>
                          {company.status !== 'approved' && (
                            <button
                              onClick={() => handleApproveCompany(company.id)}
                              className={`text-green-600 hover:text-green-900 mr-3 ${
                                getPendingDocumentsCount(company) > 0
                                  ? 'opacity-50 cursor-not-allowed'
                                  : ''
                              }`}
                              disabled={getPendingDocumentsCount(company) > 0}
                            >
                              <FontAwesomeIcon
                                icon={faCheck}
                                className="mr-1"
                              />
                              Onayla
                            </button>
                          )}
                          <button
                            onClick={() => confirmBlockCompany(company.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <FontAwesomeIcon icon={faBan} className="mr-1" />
                            Engelle
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex flex-col space-y-3 sm:hidden">
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                          currentPage === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        Önceki
                      </button>
                      <div className="flex items-center">
                        <span className="text-sm text-gray-700 mr-2">
                          Sayfa başına:
                        </span>
                        <select
                          value={companiesPerPage}
                          onChange={handleCompaniesPerPageChange}
                          className="border border-gray-300 rounded-md text-sm p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="5">5</option>
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </select>
                      </div>
                      <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                          currentPage === totalPages
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        Sonraki
                      </button>
                    </div>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div className="flex items-center">
                      <p className="text-sm text-gray-700 mr-4">
                        Toplam{' '}
                        <span className="font-medium">{companies.length}</span>{' '}
                        şirketten{' '}
                        <span className="font-medium">
                          {indexOfFirstCompany + 1}
                        </span>{' '}
                        -{' '}
                        <span className="font-medium">
                          {Math.min(
                            indexOfLastCompany,
                            filteredCompanies.length
                          )}
                        </span>{' '}
                        arası gösteriliyor
                      </p>
                      <div className="flex items-center">
                        <span className="text-sm text-gray-700 mr-2">
                          Sayfa başına:
                        </span>
                        <select
                          value={companiesPerPage}
                          onChange={handleCompaniesPerPageChange}
                          className="border border-gray-300 rounded-md text-sm p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="5">5</option>
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        {Array.from({ length: totalPages }, (_, i) => (
                          <button
                            key={i + 1}
                            onClick={() => paginate(i + 1)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === i + 1
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {i + 1}
                          </button>
                        ))}
                      </nav>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                {selectedCompany ? 'Şirket Düzenle' : 'Yeni Şirket Ekle'}
              </h3>
              <form onSubmit={handleEditSubmit}>
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Şirket Adı
                  </label>
                  <input
                    type="text"
                    value={editFormData.company_name}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        company_name: e.target.value,
                      })
                    }
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Vergi No
                  </label>
                  <input
                    type="text"
                    value={editFormData.tax_number}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        tax_number: e.target.value,
                      })
                    }
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Telefon
                  </label>
                  <input
                    type="text"
                    value={editFormData.phone_number}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        phone_number: e.target.value,
                      })
                    }
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    E-posta
                  </label>
                  <input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        email: e.target.value,
                      })
                    }
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Adres
                  </label>
                  <textarea
                    value={editFormData.address}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        address: e.target.value,
                      })
                    }
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                  >
                    {selectedCompany ? 'Güncelle' : 'Ekle'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Sürücü Atama Modalı */}
      {isDriverModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full">
          <div className="relative top-20 mx-auto p-5 border w-[600px] shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                Sürücü Ata - {selectedCompany.company_name}
              </h3>
              <div className="max-h-[60vh] overflow-y-auto">
                {drivers.length === 0 ? (
                  <p className="text-gray-500 text-center">Sürücü bulunamadı</p>
                ) : (
                  drivers.map((driver) => (
                    <div
                      key={driver.id}
                      className="flex justify-between items-center p-4 hover:bg-gray-50 border-b"
                    >
                      <div className="flex-grow">
                        <div className="flex items-center gap-4">
                          <div className="flex-grow">
                            <p className="font-medium text-lg">
                              {driver.first_name} {driver.last_name}
                            </p>
                            <div className="grid grid-cols-2 gap-x-8 gap-y-2 mt-2 text-sm">
                              <div>
                                <span className="text-gray-500">Telefon: </span>
                                <span>{driver.phone_number}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">TC No: </span>
                                <span>{driver.citizenship}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">Durum: </span>
                                <span
                                  className={`${
                                    driver.company_id
                                      ? 'text-orange-600'
                                      : 'text-green-600'
                                  }`}
                                >
                                  {driver.company_id ? 'Atanmış' : 'Müsait'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDriverDetailsClick(driver)}
                          className="px-3 py-1 rounded text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-600"
                        >
                          Detaylar
                        </button>
                        <button
                          onClick={() => handleAssignDriver(driver.id)}
                          disabled={driver.company_id}
                          className={`px-4 py-2 rounded text-sm font-medium ${
                            driver.company_id
                              ? 'bg-gray-300 cursor-not-allowed'
                              : 'bg-blue-500 hover:bg-blue-700 text-white'
                          }`}
                        >
                          {driver.company_id ? 'Atanmış' : 'Ata'}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setIsDriverModalOpen(false)}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Driver Details Modal */}
      <DriverInfoModal
        isOpen={isDriverDetailsModalOpen}
        onClose={() => setIsDriverDetailsModalOpen(false)}
        driver={selectedDriver}
      />

      {/* Document Approval Modal */}
      {isDocumentApprovalModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-[800px] shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  Belge İnceleme - {selectedCompany.company_name}
                </h3>
                <button
                  onClick={() => setIsDocumentApprovalModalOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <FontAwesomeIcon icon={faBan} />
                </button>
              </div>

              <div className="mb-4">
                <h4 className="font-medium text-gray-700 mb-2">
                  {documentType === 'l2_document' && 'L2 Belgesi'}
                  {documentType === 'tio_document' && 'TIO Belgesi'}
                  {documentType === 'c2_document' && 'C2 Belgesi'}
                </h4>

                {documentUrl ? (
                  <div className="border rounded-md p-2 mb-4">
                    <iframe
                      src={documentUrl}
                      className="w-full h-[60vh]"
                      title="Document Preview"
                    />
                  </div>
                ) : (
                  <div className="text-center py-10 bg-gray-100 rounded-md">
                    <p className="text-gray-500">Belge bulunamadı</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() =>
                    handleRejectDocument(selectedCompany.id, documentType)
                  }
                  className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
                >
                  Reddet
                </button>
                <button
                  onClick={() =>
                    handleApproveDocument(selectedCompany.id, documentType)
                  }
                  className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
                >
                  Onayla
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Update CompanyInfoModal to include document approval functionality */}
      <CompanyInfoModal
        isOpen={isCompanyDetailsModalOpen}
        onClose={() => setIsCompanyDetailsModalOpen(false)}
        company={selectedCompany}
        onSave={fetchCompanies}
        onApproveDocument={(docType) => {
          setIsCompanyDetailsModalOpen(false);
          openDocumentApprovalModal(selectedCompany, docType);
        }}
      />
    </div>
  );
}

export default Companies;
